// Lightweight XSS-safe markdown renderer
// Ported from mech-plane, restyled for light theme

function escapeHtml(str: string): string {
  return str
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;");
}

function slugifyHeadingText(text: string): string {
  const normalized = text.replace(/\s*\[Sources:\s*\d+\]\([^)]+\)\s*$/gi, "");
  const explicitAnchorMatch = normalized.match(/\s+\{#([a-z0-9-]+)\}\s*$/i);
  if (explicitAnchorMatch?.[1]) return explicitAnchorMatch[1];
  return normalized
    .replace(/\[([^\]]+)\]\([^)]+\)/g, "$1")
    .replace(/[*_`]/g, "")
    .replace(/&[a-z]+;/gi, " ")
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "");
}

function stripExplicitAnchorSuffix(text: string): string {
  return text
    .replace(/\s*\[Sources:\s*\d+\]\([^)]+\)\s*$/gi, "")
    .replace(/\s+\{#[a-z0-9-]+\}\s*$/i, "");
}

function tableCells(row: string): string[] {
  return row.trim().replace(/^\||\|$/g, "").split("|").map((cell) => cell.trim());
}

/** Only the leading title already supplied by the surrounding reader is redundant.
 * Keep the document unchanged for editing/export and preserve its heading anchor.
 */
function displayMarkdown(content: string, titleAlreadyShown?: string): string {
  const leadingTitle = content.match(/^(?:\uFEFF)?(?:[ \t]*\r?\n)*# ([^\r\n]+)(?:\r?\n|$)/);
  if (!titleAlreadyShown || !leadingTitle
    || stripExplicitAnchorSuffix(leadingTitle[1]!).trim().toLowerCase() !== titleAlreadyShown.trim().toLowerCase()) {
    return renderMarkdown(content);
  }
  const anchor = slugifyHeadingText(escapeHtml(leadingTitle[1]!));
  return `<span id="${anchor}"></span>${renderMarkdown(content.slice(leadingTitle[0].length))}`;
}

export function Markdown({ content, className = "", reading = false, titleAlreadyShown }: { content: string; className?: string; reading?: boolean; titleAlreadyShown?: string }) {
  const html = displayMarkdown(content, titleAlreadyShown);
  return (
    <div
      data-ui-audit-scope="content"
      className={`prose prose-slate min-w-0 break-words ${reading ? "max-w-[68ch] text-base leading-7 [&_p]:mb-5 [&_h1]:text-3xl [&_h2]:mt-8 [&_h2]:text-2xl [&_h3]:text-xl [&_li]:mb-2" : "prose-sm max-w-none"} ${className}`}
      dangerouslySetInnerHTML={{ __html: html }}
    />
  );
}

function renderMarkdown(md: string): string {
  const escaped = escapeHtml(md);
  let html = escaped;
  const protectedBlocks: string[] = [];
  let protectedPrefix = "HCMDPROTECTED";
  while (escaped.includes(protectedPrefix)) protectedPrefix += "X";
  const protect = (value: string) => { const key = `${protectedPrefix}${protectedBlocks.length}END`; protectedBlocks.push(value); return key; };

  // Code blocks (```[lang]\n...\n```) — allow optional whitespace after fence; require newline before body
  html = html.replace(
    /```(\w*)\s*\r?\n([\s\S]*?)```/g,
    (_, lang, code) =>
      protect(`<pre aria-label="Report code block" class="bg-slate-50 border border-slate-200 p-3 overflow-x-auto text-xs"><code class="language-${String(lang || "text").replace(/[^a-zA-Z0-9_-]/g, "")}">${code.replace(/\n$/, "")}</code></pre>`),
  );

  // Inline code (avoid matching backticks inside fenced blocks — those are already <pre>)
  html = html.replace(/`([^`]+)`/g, (_, code) => protect(`<code class="bg-slate-100 text-slate-800 px-1 py-0.5 text-sm">${code}</code>`));

  // Headers — longest prefix first so #### is not parsed as ###
  html = html.replace(/^###### (.+)$/gm, (_, text) => {
    const id = slugifyHeadingText(text);
    return `<h6 id="${id}" class="text-xs font-semibold uppercase tracking-wide text-slate-600 mt-3 mb-1">${stripExplicitAnchorSuffix(text)}</h6>`;
  });
  html = html.replace(/^##### (.+)$/gm, (_, text) => {
    const id = slugifyHeadingText(text);
    return `<h5 id="${id}" class="text-sm font-semibold text-slate-800 mt-3 mb-1">${stripExplicitAnchorSuffix(text)}</h5>`;
  });
  html = html.replace(/^#### (.+)$/gm, (_, text) => {
    const id = slugifyHeadingText(text);
    return `<h4 id="${id}" class="text-sm font-semibold text-slate-900 mt-4 mb-1">${stripExplicitAnchorSuffix(text)}</h4>`;
  });
  html = html.replace(/^### (.+)$/gm, (_, text) => {
    const id = slugifyHeadingText(text);
    return `<h3 id="${id}" class="text-base font-semibold text-slate-900 mt-4 mb-1">${stripExplicitAnchorSuffix(text)}</h3>`;
  });
  html = html.replace(/^## (.+)$/gm, (_, text) => {
    const id = slugifyHeadingText(text);
    return `<h2 id="${id}" class="text-lg font-semibold text-slate-900 mt-4 mb-2">${stripExplicitAnchorSuffix(text)}</h2>`;
  });
  html = html.replace(/^# (.+)$/gm, (_, text) => {
    const id = slugifyHeadingText(text);
    return `<h1 id="${id}" class="text-xl font-bold text-slate-900 mt-4 mb-2">${stripExplicitAnchorSuffix(text)}</h1>`;
  });

  // Bold + italic
  html = html.replace(/\*\*\*(.+?)\*\*\*/g, "<strong><em>$1</em></strong>");
  html = html.replace(/\*\*(.+?)\*\*/g, "<strong>$1</strong>");
  html = html.replace(/\*(.+?)\*/g, "<em>$1</em>");

  // Links — validate URL protocol to prevent javascript: injection
  html = html.replace(
    /\[([^\]]+)\]\(([^)]+)\)/g,
    (_, text, url) => {
      const safeUrl = /^https?:\/\//i.test(url) || /^#[a-z0-9-]+$/i.test(url) ? url : "#";
      return `<a href="${safeUrl}" target="_blank" rel="noopener noreferrer" class="text-blue-600 hover:underline">${text}</a>`;
    },
  );

  // Unordered lists
  html = html.replace(/^- (.+)$/gm, '<li class="ml-4 list-disc">$1</li>');

  // Ordered lists
  html = html.replace(/^\d+\. (.+)$/gm, '<li class="ml-4 list-decimal">$1</li>');

  // Basic GFM tables. Keep the table in a horizontal scroll region at narrow
  // widths rather than shrinking text or pretending it is a chart.
  html = html.replace(
    /^(\|[^\n]+\|)\n(\|(?:\s*:?-{3,}:?\s*\|)+)\n((?:\|[^\n]+\|(?:\n|$))+)/gm,
    (_, headerRow: string, _dividerRow: string, bodyRows: string) => {
      const headers = tableCells(headerRow);
      const rows = bodyRows.trim().split("\n").filter(Boolean).map(tableCells);
      if (headers.length < 2 || rows.some((row) => row.length !== headers.length)) return _;
      const headerHtml = headers.map((cell) => `<th scope="col" class="whitespace-nowrap px-3 py-2 text-left font-semibold text-slate-800">${cell}</th>`).join("");
      const bodyHtml = rows.map((cells) => `<tr class="border-t border-slate-100">${cells.map((cell) => `<td class="px-3 py-2 align-top">${cell}</td>`).join("")}</tr>`).join("");
      return `<div role="region" aria-label="Report table (scroll horizontally to view all columns)" tabindex="0" class="my-4 overflow-x-auto rounded-lg border border-slate-200"><table class="min-w-full text-sm"><thead class="bg-slate-50"><tr>${headerHtml}</tr></thead><tbody>${bodyHtml}</tbody></table></div>`;
    },
  );

  for (const [style, tag] of [["list-disc", "ul"], ["list-decimal", "ol"]]) {
    const item = `<li class="ml-4 ${style}">[^\\n]*<\\/li>`;
    const listPattern = new RegExp(`^${item}(?:\\n(?:\\n)?${item})*`, "gm");
    html = html.replace(listPattern, list => `<${tag} class="my-4 pl-5">${list.replace(/\n\n/g, "\n")}</${tag}>`);
  }
  html = html.replace(/^&gt;[^\n]*(?:\n&gt;[^\n]*)*/gm, quote => `<blockquote class="my-5 border-l-4 border-slate-300 pl-4 text-slate-600">${quote.replace(/^&gt; ?/gm, "").trim().replace(/\n/g, "<br>")}</blockquote>`);
  html = html.replace(/^---[ \t]*$/gm, "<hr class=\"my-6 border-slate-200\">");

  // Paragraphs (double newline)
  html = html
    .split("\n\n")
    .map((block) => {
      const trimmed = block.trim();
      if (!trimmed) return "";
      if (trimmed.startsWith("<h") || trimmed.startsWith("<pre") || trimmed.startsWith("<li") || trimmed.startsWith("<ul") || trimmed.startsWith("<ol") || trimmed.startsWith("<div") || trimmed.startsWith("<blockquote") || new RegExp(`^${protectedPrefix}\\d+END$`).test(trimmed)) return trimmed;
      const boldHeadingMatch = trimmed.match(/^<strong>(.+?)<\/strong>(?:\s+\{#([a-z0-9-]+)\})?(?:\s+(<a[^>]+>Sources:\s*\d+<\/a>))?$/i);
      if (boldHeadingMatch) {
        const text = boldHeadingMatch[1] ?? "";
        const explicitAnchor = boldHeadingMatch[2];
        const id = explicitAnchor ?? slugifyHeadingText(text);
        const body = explicitAnchor ? trimmed.replace(/\s+\{#[a-z0-9-]+\}/i, "") : trimmed;
        return `<p id="${id}" class="mb-2">${body}</p>`;
      }
      return `<p class="mb-2">${trimmed}</p>`;
    })
    .join("\n");

  // Single newlines within paragraphs → <br>
  html = html.replace(/([^>\n])\n([^<\n])/g, "$1<br>$2");

  return html.replace(new RegExp(`${protectedPrefix}(\\d+)END`, "g"), (_, index) => protectedBlocks[Number(index)] ?? "");
}
